import { getAccountingConnectionStatus } from 'client/components/AgerasAccounting/helpers'
import { UiSetup } from 'client/Interfaces/CompanyInterface'
import { type StateInterface } from 'client/Interfaces/StateInterface'
import { parse } from 'path'
import { type PersistPartial } from 'redux-persist/es/persistReducer'

import { SegmentEvents } from './SegmentEvents'

interface DefaultIdentifyProps {
  id: string
  organization_id: string
  name?: string
  created_at: string
  email: string
  current_billing_plan?: string
  current_billing_period?: string
  vat_number?: string
  current_brand: 'ageras'
  country: string
  currency?: string
  locale_id: string
  is_trial: boolean
  random_bucket?: number
  accounting_connection: string
  ui_setup: UiSetup
}

interface DefaultTrackProps {
  id: string
  organization_id: string
  name?: string
  email: string
  billing_plan?: string
  billing_period?: string
  brand: 'ageras'
  is_trial: boolean
  client: 'webapp'
  ui_setup: UiSetup
  country: string
}

type VariantControl = 'variant' | 'control'

export abstract class Segment {
  private static getState: () => StateInterface & PersistPartial // for now

  public static initialize(writeKey: string, getState) {
    this.getState = getState

    const userEmail = this.getState().user?.email
    if (!userEmail) {
      return
    }
    if (process.env.NODE_ENV !== 'production' && userEmail.includes('robot.roskis')) {
      return
    }

    if (typeof document !== 'undefined' && typeof document.createElement === 'function' && writeKey) {
      const script = document.createElement('script')
      script.innerHTML = `!function(){var i="analytics",analytics=window[i]=window[i]||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","screen","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware","register"];analytics.factory=function(e){return function(){if(window[i].initialized)return window[i][e].apply(window[i],arguments);var n=Array.prototype.slice.call(arguments);if(["track","screen","alias","group","page","identify"].indexOf(e)>-1){var c=document.querySelector("link[rel='canonical']");n.push({__t:"bpc",c:c&&c.getAttribute("href")||void 0,p:location.pathname,u:location.href,s:location.search,t:document.title,r:document.referrer})}n.unshift(e);analytics.push(n);return analytics}};for(var n=0;n<analytics.methods.length;n++){var key=analytics.methods[n];analytics[key]=analytics.factory(key)}analytics.load=function(key,n){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.setAttribute("data-global-segment-analytics-key",i);t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r);analytics._loadOptions=n};analytics._writeKey="bEa8tfFPqZ92U3PQ4c1UOwuT8py2AUPt";;analytics.SNIPPET_VERSION="5.2.0";
        analytics.load("${writeKey}");
        analytics.page();
        }}();`

      document.head.insertBefore(script, document.head.firstChild)

      if (!localStorage.getItem('afterLogin')) {
        Segment.identify()
        Segment.track(SegmentEvents.common.signedIn)
        localStorage.setItem('afterLogin', 'true')

        if (window.localStorage.getItem('WelcomeModal')) {
          Segment.track(SegmentEvents.organization.created, this.getTrackingData())
        }

        // Track the dashboard view as the analytics is not loaded at the first dashboard view
        Segment.track(SegmentEvents.common.dashboardView)
      }
    } else {
      console.error('Could not initialize Segment analytics, DOM not found.')
    }
  }

  protected static getTrackingData(): { first_touch?: string; last_touch?: string } | {} {
    const analyticsMetrics = (window as any).ageras_src?.getSourceData?.()

    if (analyticsMetrics) {
      return {
        first_touch: analyticsMetrics.first_touch.attribution,
        last_touch: analyticsMetrics.last_touch.attribution,
        entry_url: analyticsMetrics.first_touch.entry_url,
      }
    } else {
      const cognitoJsonInfo = this.getState()?.token?.['custom:info_json']
      const parsedCognitoInfo = cognitoJsonInfo && JSON.parse(cognitoJsonInfo)

      let entryUrl = window.location.href

      if (parsedCognitoInfo?.analytics) {
        const decodedUrl = decodeURIComponent(parsedCognitoInfo.analytics || '')
        const urlObj = new URL(decodedUrl)
        urlObj.search = ''
        entryUrl = urlObj.toString()
      }

      return { first_touch: parsedCognitoInfo?.analyticsAttribution, entry_url: entryUrl }
    }
  }

  protected static defaultIdentify(): DefaultIdentifyProps {
    const state = Segment.getState()
    const plan = state.subscriptionManagement?.companySubscription?.subscription?.plan
    return {
      id: state.company?.globalId,
      organization_id: state.company?.globalId,
      name: state.company?.name,
      created_at: state.company?.insertTime?.slice(0, 23), // chop two extra digits in the end
      email: state.user?.email,
      current_billing_plan: plan?.name?.replace('plan.', ''),
      current_billing_period: plan?.billingCycle,
      vat_number: state.company?.vatNumber,
      current_brand: 'ageras',
      country: state.company?.country,
      currency:
        state.config?.currencies &&
        Object.values(state.config?.currencies)?.find((c) => c.id === state.company?.currencyId)?.shortName,
      locale_id: state.company?.locale || state.token?.locale,
      is_trial: state.subscriptionManagement?.companySubscription?.subscription?.status === 'trialing',
      random_bucket: state.company?.randomBucket,
      accounting_connection: getAccountingConnectionStatus(state.company, state.settings),
      ui_setup: state.company?.uiSetup,
    }
  }

  protected static defaultTrack(): DefaultTrackProps {
    const state = Segment.getState()
    const plan = state.subscriptionManagement?.companySubscription?.subscription?.plan

    return {
      id: state.company?.globalId,
      organization_id: state.company?.globalId,
      name: state.company?.name,
      email: state.user?.email,
      billing_plan: plan?.name?.replace('plan.', ''),
      billing_period: plan?.billingCycle,
      brand: 'ageras',
      is_trial: state.subscriptionManagement?.companySubscription?.subscription?.status === 'trialing',
      client: 'webapp',
      ui_setup: state.company?.uiSetup,
      country: state.company?.country,
    }
  }

  public static identify(payload?: Record<string, string | number | boolean>): void {
    try {
      if (typeof window === 'undefined') {
        return
      }
      const defaultProps = Segment.defaultIdentify()
      if (!defaultProps?.id) {
        return
      }
      window?.analytics?.identify(defaultProps.id, { ...defaultProps, ...payload })
    } catch (err) {}
  }

  public static track(event: string, payload?: Record<string, string | number | boolean>): void {
    try {
      window?.analytics?.track(event, { ...Segment.defaultTrack(), ...payload })
    } catch (err) {}
  }

  public static page(route): void {
    try {
      window?.analytics?.page('Webapp Page', { route })
    } catch (err) {}
  }

  public static reset() {
    window?.analytics?.reset()
  }
}
